/**
 * TokenSpendIndicator — compact pill showing the signed-in user's
 * Doubloons balance plus a "Buy more" link to the buy-tokens page.
 *
 * Render at the top-right of any panel/page where the user can take a
 * token-spending action (promotion cart/composer, redeem buttons, etc.).
 *
 * Returns null when no user is signed in.
 */
import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCoins, faPlus } from "@fortawesome/free-solid-svg-icons";
import useTokenBalance from "../hooks/useTokenBalance";
import "../styles/TokenSpendIndicator.css";

const DEFAULT_LOW_THRESHOLD = 10;

export default function TokenSpendIndicator({
  userId,
  src = "spend-indicator",
  lowThreshold = DEFAULT_LOW_THRESHOLD,
  className = "",
}) {
  const { balance, loading } = useTokenBalance(userId);

  if (!userId) return null;

  const isLow = balance !== null && balance < lowThreshold;
  const buyHref = `/tokens?src=${encodeURIComponent(src)}&intent=topup`;

  return (
    <div
      className={`token-spend-indicator${isLow ? " token-spend-indicator--low" : ""}${
        className ? ` ${className}` : ""
      }`}
      role="status"
      aria-label={`Doubloons available: ${loading ? "loading" : balance ?? 0}`}
    >
      <span className="token-spend-indicator__balance">
        <FontAwesomeIcon icon={faCoins} className="token-spend-indicator__icon" />
        <span className="token-spend-indicator__count">
          {loading ? "…" : (balance ?? 0).toLocaleString()}
        </span>
        <span className="token-spend-indicator__label">Doubloons</span>
      </span>
      <span className="token-spend-indicator__sep" aria-hidden="true">
        ·
      </span>
      <Link
        to={buyHref}
        className="token-spend-indicator__buy"
        title="Buy more Doubloons"
      >
        <FontAwesomeIcon icon={faPlus} />
        <span>Buy more</span>
      </Link>
    </div>
  );
}
